import React from 'react';
import { MapPin, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import type { NearMeStatus } from '../../hooks/useNearMe';
import { NEAR_ME_STATUS } from '../../hooks/useNearMe';

type Props = {
  open: boolean;
  status: NearMeStatus;
  onAllow: () => void;
  onDismiss: () => void;
  variant?: 'floating' | 'inline';
};

const FLOATING_WRAPPER_CLASS = 'fixed top-4 left-4 z-50 w-[320px] max-w-[calc(100vw-32px)]';
const INLINE_WRAPPER_CLASS =
  'mt-3 animate-in fade-in slide-in-from-top-1 duration-200 ease-out';

const COPY = {
  title: 'Find listings near you',
  body: "We'll use your device location to show what's close. Your location stays in your browser.",
  allow: 'Allow location',
  asking: 'Asking…',
  skip: 'Skip',
  denied: 'Location turned off. Re-enable it in your browser settings, then try again.',
  unavailable: 'Location services unavailable on this device.',
};

const StatusHelper = ({ text }: { text: string | null }) =>
  text ? <p className="mt-2 text-xs text-destructive">{text}</p> : null;

const ActionButtons = ({ isAsking, isUnavailable, onAllow, onDismiss }: {
  isAsking: boolean;
  isUnavailable: boolean;
  onAllow: () => void;
  onDismiss: () => void;
}) => (
  <div className="mt-3 flex gap-2">
    <Button
      type="button"
      size="sm"
      onClick={onAllow}
      disabled={isAsking || isUnavailable}
      data-testid="location-permission-allow"
    >
      {isAsking ? COPY.asking : COPY.allow}
    </Button>
    <Button
      type="button"
      size="sm"
      variant="ghost"
      onClick={onDismiss}
      data-testid="location-permission-skip"
    >
      {COPY.skip}
    </Button>
  </div>
);

export const LocationPermissionGate = ({
  open,
  status,
  onAllow,
  onDismiss,
  variant = 'floating',
}: Props) => {
  if (!open) return null;

  const isAsking = status === NEAR_ME_STATUS.Asking;
  const isDenied = status === NEAR_ME_STATUS.Denied;
  const isUnavailable = status === NEAR_ME_STATUS.Unavailable;
  const helperText = isDenied ? COPY.denied : isUnavailable ? COPY.unavailable : null;

  if (variant === 'inline') {
    return (
      <div
        role="region"
        aria-labelledby="cys-near-me-title"
        className={INLINE_WRAPPER_CLASS}
        data-testid="location-permission-gate"
      >
        <p
          id="cys-near-me-title"
          className="text-sm text-muted-foreground"
        >
          {COPY.body}
        </p>
        {helperText && <p className="mt-2 text-sm text-destructive">{helperText}</p>}
        <div className="mt-3 flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onDismiss}
            className="font-heading uppercase tracking-wide"
            data-testid="location-permission-skip"
          >
            {COPY.skip}
          </Button>
          <Button
            type="button"
            onClick={onAllow}
            disabled={isAsking || isUnavailable}
            className="font-heading uppercase tracking-wide gap-1.5"
            data-testid="location-permission-allow"
          >
            <MapPin className="h-4 w-4" />
            {isAsking ? COPY.asking : COPY.allow}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cys-near-me-title"
      className={FLOATING_WRAPPER_CLASS}
      data-testid="location-permission-gate"
    >
      <Card className="relative p-4 shadow-lg">
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
          data-testid="location-permission-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
            <MapPin className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 id="cys-near-me-title" className="font-heading text-sm font-semibold uppercase tracking-wide">
              {COPY.title}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">{COPY.body}</p>
            <StatusHelper text={helperText} />
            <ActionButtons
              isAsking={isAsking}
              isUnavailable={isUnavailable}
              onAllow={onAllow}
              onDismiss={onDismiss}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};
